import ROUTES from "const/routes";
import {
  useAuthorize,
  useInitUserQuery,
  useUserPermissions,
} from "graphql/user";
import { useSyncChatwootUser } from "hooks/useSyncChatwootUser";
import { EmployeeDetailsLayout } from "pages/Companies/CompanyId/Employees/EmployeeId/components/EmployeeDetailsLayout";
import { CreateCompanySourceType } from "pages/Companies/New/utils/create-company.utils";
import { ComplianceLayout } from "pages/Controls/Compliance/page/compliance-layout.page";
import { NotFound } from "pages/NotFound/page/not-found.page";
import { BannerProvider } from "providers/BannerProvider";
import { DashboardTourProvider } from "providers/DashboardTourProvider";
import { EditTranslationProvider } from "providers/EditTranslationProvider";
import { EmployeesTourProvider } from "providers/EmployeesTourProvider";
import { useFeature } from "providers/FeaturesProvider";
import type { FC, JSX } from "react";
import { lazy, Suspense } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { PermissionEnum } from "types";
import "../internal-resources";
import {
  COMPANY_REQUESTS_PERMISSIONS,
  EMPLOYEE_REQUESTS_PERMISSIONS,
  REQUESTS_PERMISSIONS,
} from "../layouts/admin/LayoutOuterSidebar/constants";
import { PageLayout } from "../layouts/PageLayout";
import { generateInternalResourceRoutes } from "../lib/internal-resources";
import { AdminEntry } from "./AdminEntry";
import { ChatPanelProvider } from "./ChatPanelProvider";
import { lazyPage } from "./lazyPage";
import { RouteValidator } from "./RouteValidator";

const Chats = lazyPage(() =>
  import("pages/Chats/page/chats.page").then((m) => ({ default: m.Chats })),
);
const DashboardOverview = lazyPage(
  () =>
    import(
      "pages/Companies/CompanyId/Dashboard/Overview/page/dashboard-overview.page"
    ),
);
const Dashboard = lazyPage(() =>
  import(
    "pages/Companies/CompanyId/Dashboard/Overview/page/dashboard.page"
  ).then((m) => ({ default: m.Dashboard })),
);
const CompanyBillingDetailsPage = lazyPage(
  () =>
    import(
      "pages/Companies/CompanyId/Details/BillingDetails/page/billing-details.page"
    ),
);
const CompanyFeesPage = lazyPage(
  () => import("pages/Companies/CompanyId/Details/Fees/page"),
);
const CompanySettings = lazyPage(() =>
  import("pages/Companies/CompanyId/Details/Overview/page/details.page").then(
    (m) => ({ default: m.CompanySettings }),
  ),
);
const CompanyOverviewSettings = lazyPage(
  () => import("pages/Companies/CompanyId/Details/Overview/page/overview.page"),
);
const CompanySecurity = lazyPage(
  () => import("pages/Companies/CompanyId/Details/Security/page/security.page"),
);
const TaxInformation = lazyPage(
  () =>
    import(
      "pages/Companies/CompanyId/Details/TaxInformation/page/tax-information.page"
    ),
);
const CompanyAdminsSettings = lazyPage(
  () => import("pages/Companies/CompanyId/Details/Users/page/users.page"),
);
const CompanyVerificationPage = lazyPage(
  () =>
    import(
      "pages/Companies/CompanyId/Details/Verification/page/verification.page"
    ),
);
const BeneficiariesPage = lazyPage(() =>
  import(
    "pages/Companies/CompanyId/Employees/EmployeeId/Beneficiaries/page/beneficiaries.page"
  ).then((m) => ({ default: m.BeneficiariesPage })),
);
const EmploymentDetailsPage = lazyPage(
  () =>
    import(
      "pages/Companies/CompanyId/Employees/EmployeeId/EmploymentDetails/page/employment-details.page"
    ),
);
const PersonalDetailsPage = lazyPage(
  () =>
    import(
      "pages/Companies/CompanyId/Employees/EmployeeId/PersonalDetails/page/personal-details.page"
    ),
);
const EmployeeProductsPage = lazyPage(() =>
  import(
    "pages/Companies/CompanyId/Employees/EmployeeId/Products/page/products.page"
  ).then((m) => ({ default: m.EmployeeProductsPage })),
);
const ProductAssignmentDetailsPage = lazyPage(
  () =>
    import(
      "pages/Companies/CompanyId/Employees/EmployeeId/ProductAssignment/page/product-assignment-details.page"
    ),
);
const CreateEmployee = lazyPage(
  () =>
    import("pages/Companies/CompanyId/Employees/New/page/create-employee.page"),
);
const Employees = lazyPage(
  () => import("pages/Companies/CompanyId/Employees/page/employees.page"),
);
const CompanyPayments = lazyPage(
  () => import("pages/Companies/CompanyId/Payments/page/company-payments.page"),
);
const PaymentDetailsPage = lazyPage(
  () => import("pages/Companies/CompanyId/Payments/page/payment-details.page"),
);
const ProductsCreatePage = lazyPage(
  () =>
    import(
      "pages/Companies/CompanyId/Products/Create/page/products-create.page"
    ),
);
const CompanyProductsPage = lazyPage(
  () => import("pages/Companies/CompanyId/Products/page/products.page"),
);
const ProductDisplayPage = lazyPage(
  () =>
    import(
      "pages/Companies/CompanyId/Products/ProductInstanceId/Display/page/display.page"
    ),
);
const ProductInstancePage = lazyPage(
  () =>
    import(
      "pages/Companies/CompanyId/Products/ProductInstanceId/page/product-instance.page"
    ),
);
const CompanyEmployeeSubscriptionRequests = lazyPage(
  () =>
    import(
      "pages/Companies/CompanyId/Requests/page/employee-subscription-requests.page"
    ),
);
const CreateCompanyDetailsPage = lazyPage(() =>
  import("pages/Companies/New/page/ae/create-company-details.page").then(
    (m) => ({ default: m.CreateCompanyDetailsPage }),
  ),
);
const CreateCompany = lazyPage(() =>
  import("pages/Companies/New/page/create-company.page").then((m) => ({
    default: m.CreateCompany,
  })),
);
const Companies = lazyPage(() => import("pages/Companies/page/companies.page"));
const AccessAndRestrictionsPage = lazyPage(
  () =>
    import(
      "pages/Controls/AccessAndRestrictions/page/access-and-restrictions.page"
    ),
);
const AutomationsPage = lazyPage(() =>
  import("pages/Controls/Automations/page/automations.page").then((m) => ({
    default: m.AutomationsPage,
  })),
);
const BrandingPage = lazyPage(() =>
  import("pages/Controls/Branding/page/branding.page").then((m) => ({
    default: m.BrandingPage,
  })),
);
const ComplianceRoot = lazyPage(() =>
  import("pages/Controls/Compliance/page/compliance-root.page").then((m) => ({
    default: m.ComplianceRoot,
  })),
);
const KybCompanyDocumentsPage = lazyPage(() =>
  import("pages/Controls/Compliance/page/kyb-company-documents.page").then(
    (m) => ({ default: m.KybCompanyDocumentsPage }),
  ),
);
const KybSignatoryDocumentsPage = lazyPage(() =>
  import("pages/Controls/Compliance/page/kyb-signatory-documents.page").then(
    (m) => ({ default: m.KybSignatoryDocumentsPage }),
  ),
);
const KycDocumentTypesPage = lazyPage(() =>
  import("pages/Controls/Compliance/page/kyc-document-types.page").then(
    (m) => ({ default: m.KycDocumentTypesPage }),
  ),
);
const CustomAttributesEdit = lazyPage(
  () => import("pages/Controls/CustomAttributes/CategoryId/page/edit.page"),
);
const CustomAttributes = lazyPage(
  () => import("pages/Controls/CustomAttributes/page/custom-attributes.page"),
);
const DocumentsPage = lazyPage(
  () => import("pages/Controls/Documents/page/documents.page"),
);
const EditEmail = lazyPage(() =>
  import("pages/Controls/Emails/EmailType/page/edit-email.page").then((m) => ({
    default: m.EditEmail,
  })),
);
const Emails = lazyPage(() =>
  import("pages/Controls/Emails/page/emails.page").then((m) => ({
    default: m.Emails,
  })),
);
const FeesPage = lazyPage(() => import("pages/Controls/Fees/index"));
const PaymentsPage = lazyPage(
  () => import("pages/Controls/Payments/page/payments.page"),
);
const PlatformProductsPage = lazyPage(
  () => import("pages/Controls/Products/page/products-new.page"),
);
const PlatformProductDetailsPage = lazyPage(
  () =>
    import(
      "pages/Controls/Products/ProductId/Details/page/product-details.page"
    ),
);
const ProductCustomAttributesPage = lazyPage(
  () =>
    import(
      "pages/Controls/Products/ProductId/ProductCustomAttributes/page/product-custom-attributes.page"
    ),
);
const PlatformProductWorkflowsPage = lazyPage(
  () =>
    import(
      "pages/Controls/Products/ProductId/Workflows/page/platform-product-workflows.page"
    ),
);
const SupportToolsPage = lazyPage(
  () => import("pages/Controls/SupportTools/page/support-tools.page"),
);
const TasksPage = lazyPage(() =>
  import("pages/Controls/Tasks").then((m) => ({ default: m.TasksPage })),
);
const ActivityConfigPage = lazyPage(
  () => import("pages/Controls/Activity/page/activity-config.page"),
);
const ActivityPage = lazyPage(
  () => import("pages/Activity/page/activity.page"),
);
const CompanyActivityPage = lazyPage(
  () => import("pages/Activity/page/company-activity.page"),
);
const NewWorkflowPage = lazyPage(() =>
  import("pages/Controls/Workflow/New/page/create-workflow.page").then((m) => ({
    default: m.NewWorkflowPage,
  })),
);
const DisclaimersPage = lazyPage(() =>
  import("pages/Disclaimers/page/disclaimers.page").then((m) => ({
    default: m.DisclaimersPage,
  })),
);
const Holdings = lazyPage(() =>
  import("pages/Holdings").then((m) => ({ default: m.Holdings })),
);
const MarketOrderDetails = lazyPage(
  () =>
    import("pages/MarketOrders/MarketOrderId/page/market-order-details.page"),
);
const MarketOrders = lazyPage(
  () => import("pages/MarketOrders/page/market-orders.page"),
);
const Payments = lazyPage(() => import("pages/Payments/page/payments.page"));
const Profile = lazyPage(() => import("pages/Profile/page/profile.page"));
const CompanyRequestDetails = lazyPage(
  () =>
    import(
      "pages/Requests/Company/CompanyId/page/company-request-details.page"
    ),
);
const EmployeeRequestDetails = lazyPage(() =>
  import("pages/Requests/Employee/RequestId").then((m) => ({
    default: m.EmployeeRequestDetails,
  })),
);
const Requests = lazyPage(() => import("pages/Requests/page/requests.page"));
const ManageRole = lazyPage(() =>
  import("pages/UsersManagement/Roles/Create/page/manage-role.page").then(
    (m) => ({ default: m.ManageRole }),
  ),
);
const RolesList = lazyPage(() =>
  import("pages/UsersManagement/Roles/page/roles.page").then((m) => ({
    default: m.RolesList,
  })),
);
const UsersManagement = lazyPage(() =>
  import("pages/UsersManagement/Users/page/users-management.page").then(
    (m) => ({ default: m.UsersManagement }),
  ),
);
const UsersTablePage = lazyPage(() =>
  import("pages/UsersManagement/Users/page/users-table.page").then((m) => ({
    default: m.UsersTablePage,
  })),
);
const UserDetailsPage = lazyPage(
  () => import("pages/UsersManagement/Users/UserId/page/user-details.page"),
);
const FormDetailsPage = lazyPage(
  () => import("../pages/Controls/Forms/page/form-details.page"),
);
const FormEditorPage = lazyPage(
  () => import("../pages/Controls/Forms/page/form-editor.page"),
);
const FormsListPage = lazyPage(
  () => import("../pages/Controls/Forms/page/form-list.page"),
);
const FormsPage = lazyPage(
  () => import("../pages/Controls/Forms/page/forms.page"),
);
const CompanyDetailsPage = lazyPage(
  () => import("../pages/HoldingsDetails/CompanyDetails"),
);
const HoldingsDetailsPage = lazyPage(
  () => import("../pages/HoldingsDetails/EmployeeDetails"),
);
const LiaWasHere = lazy(() => import("../pages/Sandbox/pages/lia-was-here"));
const ZoeBibble = lazy(() => import("../pages/Sandbox/pages/zoe-bibble"));
const Sandbox = lazy(() => import("../pages/Sandbox"));

const GlobalTranslationsPage = lazyPage(
  () => import("../pages/Controls/Translations"),
);
const Tasks = lazyPage(() => import("../pages/Tasks"));
const TasksDetails = lazyPage(() => import("../pages/TaskDetails"));
const TaskOverview = lazyPage(
  () => import("../pages/TaskDetails/components/TaskOverview"),
);
const Activity = lazyPage(
  () => import("../pages/Companies/CompanyId/Activity"),
);
const FeeSettingsPage = lazyPage(
  () => import("../pages/Controls/Fees/FeeId/Settings"),
);
const FeeWorkflowsPage = lazyPage(
  () => import("../pages/Controls/Fees/FeeId/Workflows"),
);
const FeeWorkflowSettingsPage = lazyPage(
  () => import("../pages/Controls/Fees/FeeId/Workflows/WorkflowId/Settings"),
);
const useCompanyRoutes = () => {
  const beneficiariesPermitted = useAuthorize(
    PermissionEnum.ViewEmployeeNominees,
  );
  const canManageVerification = useAuthorize(PermissionEnum.CreateKybTask);
  const isActivityEnabled = useFeature("activity_feature");

  return [
    <Route
      element={
        <DashboardTourProvider>
          <Dashboard />
        </DashboardTourProvider>
      }
      key={ROUTES.COMPANIES_DASHBOARD}
      path="dashboard"
    >
      <Route element={<DashboardRoutes />} path="*" />
    </Route>,
    <Route
      element={<Activity />}
      key={ROUTES.COMPANIES_ACTIVITY}
      path={"/" + ROUTES.COMPANIES_ACTIVITY}
    />,
    isActivityEnabled && (
      <Route
        element={<CompanyActivityPage />}
        key={ROUTES.COMPANIES_FUND_ACTIVITY}
        path={"/" + ROUTES.COMPANIES_FUND_ACTIVITY}
      />
    ),
    canManageVerification && (
      <Route
        element={<CompanyVerificationPage />}
        key={ROUTES.COMPANIES_VERIFICATION}
        path={"/" + ROUTES.COMPANIES_VERIFICATION}
      />
    ),
    <Route
      element={<Employees />}
      key={ROUTES.COMPANIES_EMPLOYEES}
      path={"/" + ROUTES.COMPANIES_EMPLOYEES}
    />,
    <Route
      element={<CompanyPayments />}
      key={ROUTES.COMPANIES_PAYMENTS}
      path={"/" + ROUTES.COMPANIES_PAYMENTS}
    />,
    <Route
      element={<CompanyEmployeeSubscriptionRequests />}
      key={ROUTES.COMPANIES_REQUESTS}
      path={"/" + ROUTES.COMPANIES_REQUESTS}
    />,
    <Route
      element={<PaymentDetailsPage />}
      key={ROUTES.COMPANIES_PAYMENT_DETAILS}
      path={"/" + ROUTES.COMPANIES_PAYMENT_DETAILS}
    />,
    <Route
      element={<ProductAssignmentDetailsPage />}
      key={ROUTES.COMPANIES_EDIT_EMPLOYEE_PRODUCT_ASSIGNMENT}
      path={"/" + ROUTES.COMPANIES_EDIT_EMPLOYEE_PRODUCT_ASSIGNMENT}
    />,
    <Route key={ROUTES.COMPANIES_EMPLOYEES} path="employees">
      <Route
        element={
          <EmployeesTourProvider>
            <Employees />
          </EmployeesTourProvider>
        }
        index
      />
      <Route element={<CreateEmployee />} path="new" />
      <Route element={<EmployeeDetailsLayout />} path=":employeeId">
        <Route element={<PersonalDetailsPage />} path="personal-details" />
        <Route element={<EmploymentDetailsPage />} path="employment-details" />
        <Route element={<EmployeeProductsPage />} path="products" />
        {beneficiariesPermitted && (
          <Route element={<BeneficiariesPage />} path="beneficiaries" />
        )}
      </Route>
    </Route>,
    <Route key={ROUTES.COMPANIES_PAYMENTS} path="payments">
      <Route element={<CompanyPayments />} index />
      <Route path=":paymentId">
        <Route element={<PaymentDetailsPage />} />
      </Route>
    </Route>,
    <Route key={ROUTES.COMPANIES_PRODUCTS} path="products">
      <Route element={<CompanyProductsPage />} index />
      <Route element={<ProductsCreatePage />} path="create" />
      <Route
        element={<ProductDisplayPage />}
        path=":productInstanceId/display"
      />
      <Route
        element={<ProductInstancePage />}
        path=":productInstanceId/:step?"
      />
    </Route>,
  ];
};

export function useAdminRoutes(): JSX.Element[] | null {
  const { loading: userLoading } = useInitUserQuery();
  const { userPermissions, permissionsContain } = useUserPermissions();
  const isActivityEnabled = useFeature("activity_feature");
  useSyncChatwootUser();

  if (userLoading) return null;

  const routes: JSX.Element[] = [];

  if (permissionsContain(userPermissions, [PermissionEnum.ViewUser])) {
    routes.push(
      <Route
        element={<UsersManagement />}
        key={ROUTES.USERS_MANAGEMENT}
        path={ROUTES.USERS_MANAGEMENT}
      >
        <Route element={<Navigate to="users" replace />} index />
        <Route element={<UsersTablePage />} path="users" />
        <Route element={<UserDetailsPage />} path="users/:userId" />
      </Route>,
    );
  }

  if (permissionsContain(userPermissions, [PermissionEnum.ViewMarketOrder])) {
    routes.push(
      <Route key={ROUTES.MARKET_ORDERS} path={ROUTES.MARKET_ORDERS}>
        <Route element={<MarketOrders />} index />
        <Route element={<MarketOrderDetails />} path=":marketOrderId" />
      </Route>,
    );
  }
  if (permissionsContain(userPermissions, [PermissionEnum.ManageAiAssistant])) {
    routes.push(
      <Route key={ROUTES.CHATS} path={ROUTES.CHATS}>
        <Route element={<Chats />} index />
      </Route>,
    );
  }

  if (permissionsContain(userPermissions, [PermissionEnum.ViewFund])) {
    routes.push(
      <Route key={ROUTES.HOLDINGS} path={ROUTES.HOLDINGS}>
        <Route element={<Holdings />} index />
        <Route
          element={<HoldingsDetailsPage />}
          path="employee/:holdingsId/:isin"
        />
        <Route
          element={<CompanyDetailsPage />}
          path="company/:companyId/:isin"
        />
      </Route>,
    );
  }

  if (
    permissionsContain(userPermissions, [
      PermissionEnum.ViewCompanyPayment,
      PermissionEnum.ViewWithdrawal,
    ])
  ) {
    routes.push(
      <Route key={ROUTES.PAYMENTS} path={ROUTES.PAYMENTS}>
        <Route element={<Payments />} index />
        <Route element={<PaymentDetailsPage />} path=":paymentId" />
      </Route>,
    );

    if (isActivityEnabled) {
      routes.push(
        <Route
          key={ROUTES.ACTIVITY}
          path={ROUTES.ACTIVITY}
          element={<ActivityPage />}
        />,
      );
    }
  }

  if (permissionsContain(userPermissions, REQUESTS_PERMISSIONS)) {
    routes.push(
      <Route key={ROUTES.REQUESTS} path={ROUTES.REQUESTS}>
        <Route element={<Requests />} index />
        {permissionsContain(userPermissions, COMPANY_REQUESTS_PERMISSIONS) && (
          <Route
            element={<CompanyRequestDetails />}
            path="company/:companyId"
          />
        )}
        {permissionsContain(userPermissions, EMPLOYEE_REQUESTS_PERMISSIONS) && (
          <Route
            element={<EmployeeRequestDetails />}
            path="employee/:withdrawalRequestId"
          />
        )}
      </Route>,
    );
  }

  if (permissionsContain(userPermissions, [PermissionEnum.ManageTranslation])) {
    routes.push(
      <Route
        element={<Emails />}
        key={ROUTES.CONTROL_EMAILS}
        path={ROUTES.CONTROL_EMAILS}
      />,
    );
    routes.push(
      <Route
        element={<GlobalTranslationsPage />}
        key={ROUTES.CONTROL_TRANSLATIONS}
        path={ROUTES.CONTROL_TRANSLATIONS}
      />,
    );
    routes.push(
      <Route
        element={<EditEmail />}
        key={ROUTES.CONTROL_EMAIL_EDIT}
        path={ROUTES.CONTROL_EMAIL_EDIT}
      />,
    );
    routes.push(
      <Route
        element={<Emails />}
        key={ROUTES.CONTROL_EMAIL_GROUP}
        path={ROUTES.CONTROL_EMAIL_GROUP}
      />,
    );
    routes.push(
      <Route
        element={<Emails />}
        key={ROUTES.CONTROL_EMAIL_CATEGORY}
        path={ROUTES.CONTROL_EMAIL_CATEGORY}
      />,
    );
  }

  if (
    permissionsContain(userPermissions, [PermissionEnum.ManageSupportTools])
  ) {
    routes.push(
      <Route
        element={<SupportToolsPage />}
        key={ROUTES.CONTROL_SUPPORT_TOOLS}
        path={ROUTES.CONTROL_SUPPORT_TOOLS}
      />,
    );
  }

  if (import.meta.env.DEV) {
    routes.push(
      <Route key={ROUTES.SANDBOX} path={ROUTES.SANDBOX}>
        <Route
          index
          element={
            <Suspense fallback={null}>
              <Sandbox />
            </Suspense>
          }
        />
        <Route
          path="lia-was-here"
          element={
            <Suspense fallback={null}>
              <LiaWasHere />
            </Suspense>
          }
        />
        <Route
          path="zoe-bibble"
          element={
            <Suspense fallback={null}>
              <ZoeBibble />
            </Suspense>
          }
        />
      </Route>,
    );
  }

  routes.push(<Route path="*" element={<NotFound />} />);

  return routes;
}

const ControlsRoutes: FC = () => {
  const { userPermissions, permissionsContain } = useUserPermissions();
  const isTasksEnabled = useFeature("tasks_feature");
  const isActivityEnabled = useFeature("activity_feature");

  return (
    <Routes>
      {permissionsContain(userPermissions, [PermissionEnum.ManageBranding]) && (
        <>
          <Route element={<Navigate to="branding" replace />} index />
          <Route element={<BrandingPage />} path="branding" />
        </>
      )}
      {permissionsContain(userPermissions, [PermissionEnum.ManageDocument]) && (
        <Route element={<DocumentsPage />} path="documents" />
      )}
      {permissionsContain(userPermissions, [PermissionEnum.ManageForms]) && (
        <>
          <Route element={<CustomAttributes />} path="custom-attributes" />
          <Route
            element={<CustomAttributesEdit />}
            path="custom-attributes/:categoryId"
          />
        </>
      )}
      {permissionsContain(userPermissions, [PermissionEnum.ManageProduct]) && (
        <>
          <Route element={<PlatformProductsPage />} path="products" />
          <Route
            element={<PlatformProductDetailsPage />}
            path="products/:productId/details"
          />
          <Route
            element={<ProductCustomAttributesPage />}
            path="products/:productId/product-custom-attributes"
          />
          <Route
            element={<PlatformProductWorkflowsPage />}
            path="products/:productId/workflows"
          />
          <Route element={<PaymentsPage />} path="payments" />
          <Route element={<AutomationsPage />} path="automations" />
        </>
      )}
      {permissionsContain(userPermissions, [PermissionEnum.ListRole]) && (
        <Route element={<RolesList />} path="roles" />
      )}
      {permissionsContain(userPermissions, [PermissionEnum.CreateRole]) && (
        <Route element={<ManageRole />} path="roles/create" />
      )}
      {permissionsContain(userPermissions, [PermissionEnum.UpdateRole]) && (
        <Route element={<ManageRole />} path="roles/:roleId/edit" />
      )}
      {isActivityEnabled &&
        permissionsContain(userPermissions, [
          PermissionEnum.ManageActivityPage,
        ]) && <Route element={<ActivityConfigPage />} path="activity" />}
      {isTasksEnabled &&
        permissionsContain(userPermissions, [PermissionEnum.ManageTasks]) && (
          <Route element={<TasksPage />} path="tasks" />
        )}
      {permissionsContain(userPermissions, [PermissionEnum.ManageFees]) && (
        <>
          <Route element={<FeesPage />} path="fees" />
          <Route
            element={<Navigate to="settings" replace />}
            path="fees/:feeId"
          />
          <Route element={<FeeSettingsPage />} path="fees/:feeId/settings" />
          <Route element={<FeeWorkflowsPage />} path="fees/:feeId/workflows" />
          <Route
            element={<FeeWorkflowSettingsPage />}
            path="fees/:feeId/workflows/:workflowId/settings"
          />
        </>
      )}
      {permissionsContain(userPermissions, [
        PermissionEnum.ManageTranslation,
      ]) && <Route element={<GlobalTranslationsPage />} path="translations" />}
      {permissionsContain(userPermissions, [PermissionEnum.ManageForms]) && (
        <Route element={<FormsPage />} path="forms">
          <Route element={<FormsListPage />} path="" />
          <Route element={<FormEditorPage />} path="new" />
          <Route element={<FormEditorPage />} path="edit/:id" />
          <Route element={<FormDetailsPage />} path=":id" />
        </Route>
      )}
      {permissionsContain(userPermissions, [PermissionEnum.ManageKybTask]) && (
        <Route element={<ComplianceLayout />} path="compliance">
          <Route element={<ComplianceRoot />} path="" />
          <Route
            element={<KybCompanyDocumentsPage />}
            path="kyb-company-documents"
          />
          <Route
            element={<KybSignatoryDocumentsPage />}
            path="kyb-signatory-documents"
          />
          <Route element={<KycDocumentTypesPage />} path="kyc-document-types" />
        </Route>
      )}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

export function useControlsRoutes(): JSX.Element[] | null {
  const { loading: userLoading } = useInitUserQuery();
  useSyncChatwootUser();

  if (userLoading) return null;

  const routes: JSX.Element[] = [];

  routes.push(
    <Route
      element={<ControlsRoutes />}
      key={ROUTES.CONTROLS}
      path={`${ROUTES.CONTROLS}/*`}
    />,
  );

  return routes;
}

const SettingsTabsRoutes: FC = () => {
  return (
    <Routes>
      <Route element={<CompanyOverviewSettings />} path="overview" />
      <Route element={<CompanyAdminsSettings />} path="users" />
      <Route element={<RolesList />} path="roles" />
      <Route element={<TaxInformation />} path="tax-information" />
      <Route element={<CompanySecurity />} path="security" />
      <Route element={<CompanyVerificationPage />} path="verification" />
      <Route element={<CompanyBillingDetailsPage />} path="billing-details" />
      <Route element={<DisclaimersPage />} path="documents" />
      <Route element={<CompanyFeesPage />} path="fees" />
    </Routes>
  );
};

const DashboardRoutes = () => {
  const { userPermissions, permissionsContain } = useUserPermissions();

  const canViewDashboard = permissionsContain(userPermissions, [
    PermissionEnum.ViewCompanyDashboard,
  ]);

  return (
    <Routes>
      {canViewDashboard && (
        <Route path="overview" element={<DashboardOverview />} />
      )}
    </Routes>
  );
};

const taskRoutes = (
  <Route element={<Tasks />} path="tasks">
    <Route path=":taskId" element={<TasksDetails />}>
      <Route index element={<Navigate to="overview" replace />} />
      <Route path="overview" element={<TaskOverview />} />
    </Route>
  </Route>
);

export const AdminRoutes: FC = () => {
  const adminRoutes = useAdminRoutes();
  const controlsRoutes = useControlsRoutes();
  const companyRoutes = useCompanyRoutes();
  const canManageInternal = useAuthorize(PermissionEnum.ManageInternal);
  const internalResourceRoutes = canManageInternal
    ? generateInternalResourceRoutes()
    : null;
  const { userRoles } = useUserPermissions();
  const isTasksEnabled = useFeature("tasks_feature");
  const isCompanyAdmin = userRoles.some((userRole) => userRole.company != null);
  const canManageTasks = isTasksEnabled && !isCompanyAdmin;

  return (
    <EditTranslationProvider>
      <ChatPanelProvider>
        <BannerProvider>
          <Routes>
            <Route element={<RouteValidator />}>
              <Route
                path={ROUTES.WORKFLOW_CREATE}
                element={<NewWorkflowPage />}
              />
              <Route
                path={ROUTES.CONTROL_FEE_WORKFLOW_CREATE}
                element={<NewWorkflowPage />}
              />
              <Route
                path={ROUTES.CONTROL_FEE_WORKFLOW_EDIT}
                element={<NewWorkflowPage />}
              />

              <Route element={<AdminEntry />}>
                <Route index element={<Navigate to="companies" replace />} />
                <Route
                  element={<CreateCompany />}
                  path={ROUTES.COMPANIES_CREATE_V2}
                />
                <Route
                  element={
                    <CreateCompanyDetailsPage
                      createCompanySourceType={CreateCompanySourceType.Mohre}
                    />
                  }
                  path={ROUTES.COMPANIES_CREATE_MOHRE}
                />
                <Route
                  element={
                    <CreateCompanyDetailsPage
                      createCompanySourceType={CreateCompanySourceType.Manual}
                    />
                  }
                  path={ROUTES.COMPANIES_CREATE_MANUAL}
                />

                <Route path={ROUTES.COMPANIES}>
                  <Route element={<Companies />} index />
                  <Route
                    element={<Profile />}
                    key={ROUTES.PROFILE}
                    path={ROUTES.PROFILE}
                  />

                  <Route path=":companyId">
                    {companyRoutes}
                    {taskRoutes}
                    <Route
                      element={<Profile />}
                      key={ROUTES.PROFILE}
                      path={ROUTES.PROFILE}
                    />
                    <Route
                      element={<CompanySettings />}
                      key={ROUTES.COMPANIES_DETAILS}
                      path="details"
                    >
                      <Route element={<SettingsTabsRoutes />} path="*" />
                      <Route
                        element={<UserDetailsPage />}
                        path="users/:userId"
                      />
                    </Route>
                  </Route>
                </Route>

                {adminRoutes}
                <Route
                  element={<Profile />}
                  key={ROUTES.PROFILE}
                  path={ROUTES.PROFILE}
                />

                <Route
                  element={
                    <PageLayout>
                      <DisclaimersPage showActionButton />
                    </PageLayout>
                  }
                  path="disclaimers"
                />

                {controlsRoutes}
                <Route
                  element={<AccessAndRestrictionsPage />}
                  path={ROUTES.CONTROL_ACCESS_AND_RESTRICTIONS}
                />

                {internalResourceRoutes ?? (
                  <Route
                    path="/internal/data/*"
                    element={<Navigate to="/404" replace />}
                  />
                )}
                {canManageTasks && taskRoutes}

                <Route element={<NotFound />} path="404" />
              </Route>
            </Route>
          </Routes>
        </BannerProvider>
      </ChatPanelProvider>
    </EditTranslationProvider>
  );
};
